import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Plus } from "lucide-react";
import { useState } from "react";
import type {
  ThematicNodeRelation,
  ThematicNodeWithEntity,
  RelationType,
  NewThematicNodeRelation,
} from "@/types/thematicMaps";
import { RELATION_TYPES } from "@/types/thematicMaps";

interface RelationsTableProps {
  relations: ThematicNodeRelation[];
  nodes: ThematicNodeWithEntity[];
  loading: boolean;
  onCreateRelation: (
    data: Omit<NewThematicNodeRelation, "user_id" | "map_id">,
  ) => Promise<ThematicNodeRelation | null>;
  onDeleteRelation: (id: string) => Promise<boolean>;
}

const RELATION_LABELS: Record<RelationType, string> = {
  parent_of: "Parent of",
  sibling: "Sibling",
  prerequisite: "Prerequisite",
  next_step: "Next Step",
  comparison: "Comparison",
  glossary_ref: "Glossary Ref",
  entity_ref: "Entity Ref",
  hub_to_spoke: "Hub → Spoke",
};

export function RelationsTable({
  relations,
  nodes,
  loading,
  onCreateRelation,
  onDeleteRelation,
}: RelationsTableProps) {
  const [filterType, setFilterType] = useState<string>("all");
  const [newFromId, setNewFromId] = useState("");
  const [newToId, setNewToId] = useState("");
  const [newType, setNewType] = useState<RelationType>("sibling");
  const [newWeight, setNewWeight] = useState("5");
  const [adding, setAdding] = useState(false);

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  const filteredRelations =
    filterType === "all"
      ? relations
      : relations.filter((r) => r.relation_type === filterType);

  const handleAdd = async () => {
    if (!newFromId || !newToId || newFromId === newToId) return;
    setAdding(true);
    await onCreateRelation({
      from_node_id: newFromId,
      to_node_id: newToId,
      relation_type: newType,
      weight: Math.max(1, Math.min(10, parseInt(newWeight, 10) || 5)),
    });
    setNewFromId("");
    setNewToId("");
    setNewType("sibling");
    setNewWeight("5");
    setAdding(false);
  };

  const handleDelete = async (id: string) => {
    if (confirm("Usunąć relację?")) {
      await onDeleteRelation(id);
    }
  };

  if (loading) {
    return (
      <p className="text-sm text-muted-foreground py-4">Ładowanie relacji...</p>
    );
  }

  return (
    <div className="space-y-3">
      {/* Inline add form */}
      <div className="flex items-end gap-2">
        <Select value={newFromId} onValueChange={setNewFromId}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="From node..." />
          </SelectTrigger>
          <SelectContent>
            {nodes.map((n) => (
              <SelectItem key={n.id} value={n.id}>
                {n.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={newType}
          onValueChange={(v) => setNewType(v as RelationType)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RELATION_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {RELATION_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={newToId} onValueChange={setNewToId}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="To node..." />
          </SelectTrigger>
          <SelectContent>
            {nodes.map((n) => (
              <SelectItem key={n.id} value={n.id}>
                {n.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="Waga"
          value={newWeight}
          onChange={(e) => setNewWeight(e.target.value)}
          className="w-[70px]"
          type="number"
          min={1}
          max={10}
        />
        <Button
          size="sm"
          onClick={handleAdd}
          disabled={adding || !newFromId || !newToId || newFromId === newToId}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Dodaj
        </Button>
      </div>

      {/* Filter + count */}
      <div className="flex items-center gap-2">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtruj typ..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie typy</SelectItem>
            {RELATION_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {RELATION_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {filteredRelations.length} relacji
        </span>
      </div>

      {filteredRelations.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          Brak relacji. Użyj przycisku "Generuj relacje z AI" powyżej lub dodaj
          ręcznie.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>From</TableHead>
              <TableHead className="w-[120px]">Typ</TableHead>
              <TableHead>To</TableHead>
              <TableHead className="w-[60px]">Waga</TableHead>
              <TableHead className="w-[50px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRelations.map((rel) => {
              const fromNode = nodeMap.get(rel.from_node_id);
              const toNode = nodeMap.get(rel.to_node_id);
              return (
                <TableRow key={rel.id}>
                  <TableCell className="text-sm">
                    {fromNode?.title || rel.from_node_id.slice(0, 8)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {RELATION_LABELS[rel.relation_type]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {toNode?.title || rel.to_node_id.slice(0, 8)}
                  </TableCell>
                  <TableCell className="text-center text-sm">
                    {rel.weight}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(rel.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
